import React, { useEffect, useState, useRef, useCallback } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { useParams, useNavigate } from 'react-router-dom';
import { togglePopover } from '../../redux/Slices/popoverSlice';
import { openModal } from '../../redux/Slices/modalSlice';

import { find, isWorkspaceAdmin } from '../../utils/helpers';

import ImagePlaceholder from '../Global/ImagePlaceholder';
import useHandlePopoverClick from '../../hooks/useHandlePopoverClick';
import LastActive from '../Global/LastActive';

import './scss/Collaborators.scss';
import { fetchWorkspaces, updateWorkspace } from '../../redux/Slices/workspaceSlice';
import { showSuccess, showError } from '../../utils/snackbarHelpers';
import { showSnackBar } from '../../redux/Slices/snackbarSlice';
import { config } from '../../config';
import { fetchMembers, optimisticAddMember, optimisticRemoveMember, optimisticUpdateMemberRole } from '../../redux/Slices/memberSlice';
import Spinner from '../Global/Spinner';
import MemberOnlineStatus from '../Global/MemberOnlineStatus';
import { useSocketAction } from '../../hooks/useSocketManager';
import { ActionTypes } from '../../sockets/ActionTypes';

const Collaborators = () => {

  const [width, setWidth] = useState(() => {
    const savedWidth = localStorage.getItem('expandableColumnWidth');
    return savedWidth ? parseInt(savedWidth, 10) : 290;
  });
  const [isResizing, setIsResizing] = useState(false);
  const resizeRef = useRef(null);

  // Listen for storage changes to sync width across components
  useEffect(() => {
    const handleStorageChange = (e) => {
      if (e.key === 'expandableColumnWidth' && e.newValue) {
        setWidth(parseInt(e.newValue, 10));
      }
    };

    const handleCustomWidthChange = (e) => {
      setWidth(e.detail.width);
    };

    window.addEventListener('storage', handleStorageChange);
    window.addEventListener('expandableColumnWidthChange', handleCustomWidthChange);

    return () => {
      window.removeEventListener('storage', handleStorageChange);
      window.removeEventListener('expandableColumnWidthChange', handleCustomWidthChange);
    };
  }, []);

  const [activeTab, setActiveTab] = useState('workspace-members');
  const [filterText, setFilterText] = useState('');

  // Workspace editing state
  const [isEditingWorkspaceName, setIsEditingWorkspaceName] = useState(false);
  const [workspaceNameInput, setWorkspaceNameInput] = useState('');
  const [workspaceUpdateStatus, setWorkspaceUpdateStatus] = useState('');

  // Workspace join link state
  const [workspaceJoinLink, setWorkspaceJoinLink] = useState(null);

  const { handlePopoverClick } = useHandlePopoverClick();
  const dispatch = useDispatch();

  const { workspaces } = useSelector(state => state.workspaces);
  const { shortId } = useParams('shortId');
  const workspaceId = shortId;

  useEffect(() => {
    if (workspaces.length === 0) {
      dispatch(fetchWorkspaces()).unwrap().then((fetchedWorkspaces) => {
        if (!fetchedWorkspaces || fetchedWorkspaces.length === 0) {
        }
      }).catch((error) => {
        console.error('Error fetching workspaces:', error);
      });
    }
  }, [dispatch, workspaces.length]);

  useEffect(() => {
    dispatch(fetchWorkspaces());
  }, [dispatch, workspaceId]);


  // const collaborators = find.get(workspaces, { name: 'members', shortName: workspaceId }) || [];

  const collaborators = useSelector(state => state.member.workspaceMembers);
  const status = useSelector(state => state.member.status);
  const guests = useSelector(state => state.member.workspaceGuests);
  const { user } = useSelector((state) => state.user);

  // Track if initial load is complete
  const [initialLoadComplete, setInitialLoadComplete] = useState(false);

  let collaboratorsLimit = config.FREE_COLLABORATORS_LIMIT;

  const totalAdmins = collaborators.filter(m => m.role === 'admin').length;

  const workspace = find.get(workspaces, { name: 'workspace', workspace: workspaceId }) || {};

  const workspaceName = workspace.name || '';

  // Check if current user is workspace admin
  const currentUserId = user?.user?._id;
  const currentUserIsAdmin = isWorkspaceAdmin(workspace, currentUserId);


  if (workspace?.subscription && workspace?.subscription?.planType !== 'Free') {
    const planType = workspace.subscription.planType;

    // AppSumo and DealMirror have tier-specific seat limits
    if (planType === 'AppSumo' || planType === 'DealMirror') {
      collaboratorsLimit = workspace.subscription.allocatedSeats || config.FREE_COLLABORATORS_LIMIT;
    } else {
      // Standard, Premium, Enterprise have unlimited seats
      collaboratorsLimit = 'Unlimited';
    }
  }

  useEffect(() => {
    dispatch(fetchMembers({ type: 'workspace', id: workspaceId }));
    dispatch(fetchMembers({ type: 'guests', id: workspaceId }));
  }, [dispatch, workspaceId]);

  // Mark initial load as complete when we have data
  useEffect(() => {
    if ((collaborators.length > 0 || guests.length > 0) && status === 'succeeded') {
      setInitialLoadComplete(true);
    }
  }, [collaborators.length, guests.length, status]);

  // Real-time socket listener for workspace member changes
  useSocketAction([
    ActionTypes.MEMBER_ADDED,
    ActionTypes.MEMBER_REMOVED,
    ActionTypes.ROLE_UPDATED,
  ], (data) => {
    // Only process events for this workspace
    if (data.workspaceId !== workspaceId) return;

    // Skip self-triggered events to prevent duplicate updates
    if (data.reqUser === user?.user?._id) return;

    console.log('[Collaborators] Real-time event:', data.action, data);

    switch (data.action) {
      case ActionTypes.MEMBER_ADDED:
        // Refresh member lists when new members are added
        dispatch(fetchMembers({ type: 'workspace', id: workspaceId }));
        dispatch(fetchMembers({ type: 'guests', id: workspaceId }));

        // Refresh workspaces if current user was added
        if (data.newMembers && data.newMembers.includes(user?.user?._id)) {
          dispatch(fetchWorkspaces());
        }
        break;

      case ActionTypes.MEMBER_REMOVED:
        // Use optimistic update for member removal from socket
        if (data.memberId) {
          dispatch(optimisticRemoveMember({
            type: 'workspace',
            memberId: data.memberId
          }));
        }

        // Refresh workspaces if current user was removed
        if (data.memberId === user?.user?._id) {
          dispatch(fetchWorkspaces());
        }
        break;

      case ActionTypes.ROLE_UPDATED:
        // Use optimistic update for role changes from socket
        if (data.memberId && data.newRole && data.members) {
          // Find the updated member from the socket data
          const updatedMember = data.members.find(m =>
            m.user && (m.user._id === data.memberId || m.user === data.memberId)
          );

          if (updatedMember) {
            // Prepare member object with user data
            const memberData = updatedMember.user && typeof updatedMember.user === 'object'
              ? { ...updatedMember.user, role: data.newRole }
              : { _id: data.memberId, role: data.newRole };

            dispatch(optimisticUpdateMemberRole({
              type: 'workspace',
              memberId: data.memberId,
              newRole: data.newRole,
              member: memberData
            }));
          } else {
            // Fallback to fetching if member data is not in socket event
            dispatch(fetchMembers({ type: 'workspace', id: workspaceId }));
            dispatch(fetchMembers({ type: 'guests', id: workspaceId }));
          }
        } else {
          // Fallback to fetching if socket data is incomplete
          dispatch(fetchMembers({ type: 'workspace', id: workspaceId }));
          dispatch(fetchMembers({ type: 'guests', id: workspaceId }));
        }

        // Refresh workspaces if current user's role was updated
        if (data.memberId === user?.user?._id) {
          dispatch(fetchWorkspaces());
        }
        break;

      default:
        break;
    }
  }, [dispatch, workspaceId, user?.user?._id]);

  // Update workspace name input when workspace changes
  useEffect(() => {
    if (workspace) {
      setWorkspaceNameInput(workspace.name || '');
    }
  }, [workspace]);

  const handleInviteClick = () => {
    dispatch(openModal({ modalType: 'INVITE_WORKSPACE', modalData: { workspaceId } }));
  };

  const handleGenerateWorkspaceJoinLink = async () => {
    try {
      const response = await fetch(`${config.API_URI}/v1/workspace/${workspaceId}/generate-join-link`, {
        method: 'POST',
        headers: { 'Accept': 'application/json', 'Content-Type': 'application/json' },
        credentials: 'include',
      });
      const data = await response.json();
      if (response.ok) setWorkspaceJoinLink(data.joinLink);
      else showError(dispatch, data.message);
    } catch (error) {
      showError(dispatch, 'Failed to generate join link');
    }
  };

  const handleDeleteWorkspaceJoinLink = async () => {
    try {
      const response = await fetch(`${config.API_URI}/v1/workspace/${workspaceId}/delete-join-link`, {
        method: 'DELETE',
        credentials: 'include',
      });
      if (response.ok) {
        setWorkspaceJoinLink(null);
        showSuccess(dispatch, 'Join link deleted');
      } else {
        const data = await response.json();
        showError(dispatch, data.message);
      }
    } catch (error) {
      showError(dispatch, 'Failed to delete join link');
    }
  };

  const handleCopyWorkspaceJoinLink = () => {
    if (workspaceJoinLink) {
      navigator.clipboard.writeText(workspaceJoinLink);
      showSuccess(dispatch, 'Join link copied to clipboard!');
    }
  };

  const handleFilterChange = (e) => {
    setFilterText(e.target.value);
  };

  const removeMemberFromWorkspace = async (e, member, memberType) => {
    e.preventDefault();

    const { _id, role } = member;

    if (totalAdmins <= 1 && role === 'admin') {
      showError(dispatch, 'You can\'t remove only one admin ');
      return;
    }

    if (!workspaceId) {
      showError(dispatch, 'Workspace ID is empty');
      return;
    }

    const isConfirmed = window.confirm('Are you sure you want to remove this member from the workspace?');
    if (!isConfirmed) {
      return;
    }

    // Trello "remove from workspace but keep board access": offer to demote the
    // member to a guest (keeps their board memberships) instead of fully removing.
    // Only meaningful for actual members (not guests, not self).
    let convertToGuest = false;
    const isSelf = _id === currentUserId;
    if (memberType !== 'guest' && !isSelf) {
      convertToGuest = window.confirm(
        'Keep this person on their boards as a guest?\n\nOK = keep board access (becomes a Workspace guest)\nCancel = remove from all boards too'
      );
    }

    try {
      const response = await fetch(`${config.API_URI}/v1/workspace/${workspaceId}/member`, {
        method: 'DELETE',
        headers: {
          'Accept': 'application/json',
          'Content-Type': 'application/json',
        },
        credentials: 'include',
        body: JSON.stringify({
          workspaceId,
          memberId: _id,
          convertToGuest,
        }),
      });

      const data = await response.json();
      const messageType = response.status === 200 ? 'success' : 'error';

      if (messageType === 'success') {
        showSuccess(dispatch, data.message);
        // Refresh both lists after successful removal
        dispatch(fetchMembers({ type: 'workspace', id: workspaceId }));
        dispatch(fetchMembers({ type: 'guests', id: workspaceId }));
      } else {
        showError(dispatch, data.message);
      }
    } catch (error) {
      console.error('Error removing member:', error);
      showError(dispatch, 'Error removing member');
    }
  };



  const renderTabContent = () => {
    if (activeTab === 'workspace-members') {
      const filteredCollaborators = collaborators.filter(member =>
        member?.username?.toLowerCase().includes(filterText?.toLowerCase())
      );

      return (
        <div className="workspace-members">
          <h3>Workspace members ({filteredCollaborators.length}) <span className="icon-copy" /></h3>
          <p>Workspace members can view and join all Workspace visible boards and create new boards in the Workspace. Adding new members will automatically update your billing.</p>
          {currentUserIsAdmin && (
            <div className="invite">
              <h4>Invite members to join you</h4>
              <p>Anyone with an invite link can join this paid Workspace. You’ll be billed for each member that joins. You can also disable and create a new invite link for this Workspace at any time.</p>
              <button className="invite-link-button" onClick={handleInviteClick}>
                <span className="material-symbols-outlined">
                  group
                </span>Invite Workspace members
              </button>
              <div className="workspace-join-link">
                {workspaceJoinLink ? (
                  <div className="join-link-info">
                    <span className="material-symbols-outlined">link</span>
                    <span className="join-link-text">Anyone with the link can join as a member</span>
                    <span className="join-link-actions">
                      <button onClick={handleCopyWorkspaceJoinLink} className="link-action">Copy link</button>
                      <span className="dot-separator"> &middot; </span>
                      <button onClick={handleDeleteWorkspaceJoinLink} className="link-action delete">Delete link</button>
                    </span>
                  </div>
                ) : (
                  <div className="join-link-info">
                    <span className="material-symbols-outlined">link</span>
                    <span className="join-link-text">Share this workspace with a link</span>
                    <span className="join-link-actions">
                      <button onClick={handleGenerateWorkspaceJoinLink} className="link-action">Create link</button>
                    </span>
                  </div>
                )}
              </div>
            </div>
          )}

          <div className="members-list">
            <input
              type="text"
              placeholder="Filter by name"
              value={filterText}
              onChange={handleFilterChange}
            />
            {status === 'loading' && !initialLoadComplete ? (
              <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '80px' }}>
                <Spinner size={30} color="#3498db" speed={1.5} />
              </div>
            ) : (
              filteredCollaborators.map((member) => {
                // Determine if this member is the last admin
                const isLastAdmin = totalAdmins <= 1 && member.role === 'admin';

                return (
                  <div className="member" key={member.username}>
                    {member.profilePicture ? (
                      <div className="image-placeholder-container">
                        <img
                          src={member.profilePicture}
                          alt={member.username}
                          className="profile-pic"
                          loading="lazy"
                          style={{
                            filter: member.userStatus === 'inactive' ? 'grayscale(100%)' : 'none'
                          }}
                        />
                        {member.userStatus === 'inactive' && (
                          <span className="inactive-indicator" title="Inactive user">
                            <span className="material-symbols-outlined">schedule</span>
                          </span>
                        )}
                        {member.userStatus !== 'inactive' && <MemberOnlineStatus member={member} />}
                      </div>
                    ) : (
                      <ImagePlaceholder size={40} member={member} text={member.username} fontSize='14px' />
                    )}

                    <div className="member-info">
                      <div className="member-name">{member.name}</div>
                      <div className="member-bottom">
                        <span className="username">@{member.username}</span>
                        •
                        <LastActive member={member} />
                      </div>
                    </div>

                    <button
                      className={`view-boards-button ${member.boardCount === 0 ? 'full-deactivated' : ''}`}
                      id={`popover-board-list-${member._id}`}
                      onClick={(e) => handlePopoverClick(e, 'popoverBoardList', { member, workspaceId })}
                      data-popover-trigger
                    >
                      View boards ({member.boardCount})
                    </button>

                    <div className="admin-actions">
                      <div
                        className={`members-options ${isLastAdmin ? 'full-deactivated' : ''}`}
                        id={`popover-member-${member._id}`}
                        onClick={(e) => {
                          if (isLastAdmin) return;
                          e.stopPropagation();
                          handlePopoverClick(e, 'memberRole', {
                            memberId: member._id,
                            currentRole: member.role,
                            isClosed: false,
                            memberDeleteTarget: `popover-member-${member._id}`,
                            totalAdmins,
                            apiPath: 'workspace',
                            typeId: workspaceId,
                            canChangeRole: currentUserIsAdmin,
                          });
                        }}
                        data-popover-trigger
                        style={{ cursor: isLastAdmin ? 'not-allowed' : 'pointer' }}
                      >
                        {member.role}
                        <span className="material-symbols-outlined">keyboard_arrow_down</span>
                      </div>
                    </div>
                  </div>
                );
              })

            )}
          </div>
        </div>
      );
    }

    if (activeTab === 'guests') {
      const filteredGuests = guests.filter(guest =>
        guest?.username?.toLowerCase().includes(filterText?.toLowerCase())
      );


      return (
        <div className="workspace-members">
          <h3>Workspace guests ({filteredGuests.length}) <span className="icon-copy" /></h3>
          <p>Workspace members can view and join all Workspace visible boards and create new boards in the Workspace. Adding new members will automatically update your billing.</p>
          {currentUserIsAdmin && (
            <div className="invite">
              <h4>Invite members to join you</h4>
              <p>Anyone with an invite link can join this paid Workspace. You’ll be billed for each member that joins. You can also disable and create a new invite link for this Workspace at any time.</p>
              <button className="invite-link-button" onClick={handleInviteClick}>
                <span className="material-symbols-outlined">
                  group
                </span>Invite Workspace members
              </button>
            </div>
          )}

          <div className="members-list">
            <input
              type="text"
              placeholder="Filter by name"
              value={filterText}
              onChange={handleFilterChange}
            />
            {filteredGuests.map(member => {
              return (
                <div className="member" key={member.username}>
                  {
                    member.profilePicture ? (

                      <div className="image-placeholder-container">
                        <img
                          src={member.profilePicture}
                          alt={member.username}
                          className="profile-pic"
                          loading="lazy"
                          style={{
                            filter: member.userStatus === 'inactive' ? 'grayscale(100%)' : 'none'
                          }}
                        />
                        {member.userStatus === 'inactive' && (
                          <span className="inactive-indicator" title="Inactive user">
                            <span className="material-symbols-outlined">schedule</span>
                          </span>
                        )}
                        {member.userStatus !== 'inactive' && <MemberOnlineStatus member={member} />}
                      </div>

                    ) : (
                      <ImagePlaceholder size={40} member={member} text={member.username} fontSize='14px' />
                    )
                  }

                  <div className="member-info">
                    <div className="member-name">{member.name}</div>
                    <div className="member-bottom">
                      <span className="username">@{member.username}</span>
                      •
                      <LastActive member={member} />
                    </div>
                  </div>
                  <button
                    className="view-boards-button"
                    id={`popover-board-list-${member._id}`}
                    onClick={(e) => handlePopoverClick(e, 'popoverBoardList', { member, workspaceId })}
                    data-popover-trigger
                  >
                    View boards ({member.boardCount})
                  </button>
                  <div className="admin-actions">
                    <div
                      className="members-options"
                      id={`popover-guest-${member._id}`}
                      onClick={(e) => {
                        e.stopPropagation();
                        handlePopoverClick(e, 'memberRole', {
                          memberId: member._id,
                          currentRole: member.role,
                          isClosed: false,
                          memberDeleteTarget: `popover-guest-${member._id}`,
                          totalAdmins,
                          apiPath: 'workspace',
                          typeId: workspaceId,
                          canChangeRole: currentUserIsAdmin,
                        });
                      }}
                      data-popover-trigger
                    >
                      {member.role}
                      <span className="material-symbols-outlined">keyboard_arrow_down</span>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      );
    }

    return null; // For other tabs (e.g., join requests)
  };


  // Handle mouse down on resize handle
  const handleMouseDown = (e) => {
    setIsResizing(true);
    e.preventDefault();
  };

  // Handle mouse up for resizing
  const handleMouseUp = useCallback(() => {
    setIsResizing(false);
    document.body.style.cursor = '';
    document.body.style.userSelect = '';
    // Save the width to localStorage
    localStorage.setItem('expandableColumnWidth', width.toString());

    // Dispatch custom event for same-tab synchronization
    window.dispatchEvent(new CustomEvent('expandableColumnWidthChange', {
      detail: { width }
    }));
  }, [width, isResizing]);

  // Handle mouse move for resizing
  useEffect(() => {
    const handleMouseMove = (e) => {
      if (!isResizing) return;

      const newWidth = e.clientX;
      const minWidth = 250;
      const maxWidth = Math.min(600, window.innerWidth * 0.4); // Max 40% of screen width or 600px

      if (newWidth >= minWidth && newWidth <= maxWidth) {
        setWidth(newWidth);
      }
    };

    if (isResizing) {
      document.body.style.cursor = 'col-resize';
      document.body.style.userSelect = 'none';
      document.addEventListener('mousemove', handleMouseMove);
      document.addEventListener('mouseup', handleMouseUp);
    }

    return () => {
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
      document.body.style.cursor = '';
      document.body.style.userSelect = '';
    };
  }, [isResizing, handleMouseUp]);


  // Workspace name update handlers
  const handleEditWorkspaceName = () => {
    if (workspace) {
      setIsEditingWorkspaceName(true);
    }
  };

  const handleUpdateWorkspaceName = async () => {
    if (!workspace || !workspaceNameInput.trim() || workspaceNameInput.trim() === workspace.name) {
      setIsEditingWorkspaceName(false);
      setWorkspaceNameInput(workspace?.name || '');
      return;
    }

    try {
      setWorkspaceUpdateStatus('saving');
      await dispatch(updateWorkspace({
        workspaceId: workspace._id,
        name: workspaceNameInput.trim()
      })).unwrap();

      setWorkspaceUpdateStatus('saved');
      setIsEditingWorkspaceName(false);
      dispatch(showSnackBar({ message: 'Workspace name updated successfully', type: 'success' }));

      // Clear saved status after 2 seconds
      setTimeout(() => setWorkspaceUpdateStatus(''), 2000);
    } catch (error) {
      console.error('Failed to update workspace name:', error);
      setWorkspaceUpdateStatus('error');
      dispatch(showSnackBar({ message: 'Failed to update workspace name', type: 'error' }));

      // Reset to original name on error
      setWorkspaceNameInput(workspace?.name || '');
      setIsEditingWorkspaceName(false);

      // Clear error status after 3 seconds
      setTimeout(() => setWorkspaceUpdateStatus(''), 3000);
    }
  };

  const handleWorkspaceNameKeyDown = (e) => {
    if (e.key === 'Enter') {
      handleUpdateWorkspaceName();
    } else if (e.key === 'Escape') {
      setIsEditingWorkspaceName(false);
      setWorkspaceNameInput(workspace?.name || '');
    }
  };

  return (
    <div className="zoobbe-main-content">
      <div className="collaborators">
        <div
          className={`collaborators-tab-content-container${isResizing ? ' resizing' : ''}`}
          style={{ width: `${width - 40}px`, minWidth: `${width - 40}px` }}
        >
          <div className='tab-content'>
            <h2>Collaborators <span>{collaborators.length + guests.length} / {collaboratorsLimit}</span></h2>
            <div className="tabs">
              <div
                className={`tab ${activeTab === 'workspace-members' ? 'selected' : ''}`}
                onClick={() => setActiveTab('workspace-members')}
              >
                Workspace members ({collaborators.length})
              </div>
              <div
                className={`tab ${activeTab === 'guests' ? 'selected' : ''}`}
                onClick={() => setActiveTab('guests')}
              >
                Guests ({guests.length})
              </div>
              <div
                className={`tab ${activeTab === 'join-requests' ? 'selected' : ''}`}
                onClick={() => setActiveTab('join-requests')}
              >
                Join requests (0)
              </div>
            </div>
          </div>

          {/* Resize handle */}
          <div
            className={`expandable-column__resize-handle ${isResizing ? 'resizing' : ''}`}
            onMouseDown={handleMouseDown}
            ref={resizeRef}
          />
        </div>

        <div className="collaborators-content-container">
          <div className="collaborators-content">
            <div className="board-header">
              <div className="board-icon">
                <span className="icon-text">Z</span>
              </div>
              <div className="board-details">
                <div className="board-name">
                  {isEditingWorkspaceName ? (
                    <input
                      type="text"
                      value={workspaceNameInput}
                      onChange={(e) => setWorkspaceNameInput(e.target.value)}
                      onBlur={handleUpdateWorkspaceName}
                      onKeyDown={handleWorkspaceNameKeyDown}
                      className="workspace-name-input"
                      autoFocus
                      maxLength={50}
                    />
                  ) : (
                    <>
                      <span>{workspaceName}</span>
                      {currentUserIsAdmin && (
                        <span
                          className="material-symbols-outlined edit-icon"
                          onClick={handleEditWorkspaceName}
                          title="Edit workspace name"
                        >
                          edit
                        </span>
                      )}
                    </>
                  )}
                </div>
                <div className="board-visibility">
                  <span className="material-symbols-outlined">
                    lock
                  </span>
                  <span>Private</span>
                </div>
              </div>
            </div>

            {renderTabContent()}
          </div>
        </div>
      </div>
    </div >
  );
};

export default Collaborators;
